import React, { useState } from 'react';
import {
  Truck,
  Clock,
  Star,
  User,
  MapPin,
  AlertCircle,
  MoreVertical,
  Camera,
  Copy,
  Check,
  Activity,
} from 'lucide-react';

// --- Helper Components for Cleanliness ---

// Component for the Camera status indicators
const CameraStatus = ({ label, isConnected }) => (
  <div className="flex items-center gap-1 text-sm text-gray-600">
    <div className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
    <span>{label}</span>
  </div>
);

// Component for Equipment Pills (precise color matching)
const EquipmentPill = ({ level, status }) => {
  const getEquipmentColor = (status) => {
    switch (status) {
      // Precise Hex colors from the UI
      case "good":
        return "bg-[#ECFDF5] text-[#047857]";
      case "warning":
        return "bg-[#FFFBEB] text-[#B45309]";
      case "critical":
        return "bg-[#FEF2F2] text-[#B91C1C]";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };
  return (
    <div className={`px-2 py-1 rounded-sm text-xs font-medium whitespace-nowrap ${getEquipmentColor(status)}`}>
      {level}
    </div>
  );
};

// Component for the Section Headers (ICU on Wheels, Equipments)
const SectionHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 mb-1">
    <Icon className="w-4 h-4 text-gray-600" />
    <span className="text-sm font-semibold text-gray-800">{title}</span>
  </div>
);


// --- Main AmbulanceCard Component ---
const AmbulanceCard = ({
  // Basic Info
  ambulanceId = "GJ 18 AB 2828",
  vehicleId = null,
  plateNumber = null,
  vehicleType = null,
  time = "10:04 PM",
  distance = "4.8 km",
  duration = "15min",

  // Status
  status = "Active Round Journey",
  isCritical = true,

  // Camera Status
  dashCamera = true,
  driverCamera = true,
  patientCamera = true,

  // Equipment Status (Status objects are moved inside the main component)
  fuel = { level: "Full Fuel", status: "good" },
  oxygen = { level: "500 kg Oxygen", status: "critical" },
  alcohol = { level: "200 L Alcohol", status: "warning" },
  nitrogen = { level: "500 kg Nitrogen", status: "good" },
  temperature = { level: "24° C Temperature", status: "warning" },

  // Driver Info
  driverName = "Ramesh Driver",
  avgResponseTime = "10:30 Mins",
  rating = "4.7/5",

  // Map Info
  mapLocation = "5th Ave N",
  mapSubLocation = "United States Postal Service",

  // **NEW DYNAMIC PROPS**
  // Set default colors based on the top card in the image
  cardBgColor = 'bg-[#FDF2F8]', // Light Pink/Purple for the top card (subtle)
  cardBorderColor = 'border-pink-200', // Subtle light pink border (matching the card in the screenshot)

  // Actions
  onCallDriver = () => console.log('Call driver'),
  onViewHistory = () => console.log('View history'),
  onViewProfile = () => console.log('View profile'),
  onViewMore = () => console.log('View more'),

  // Map click handler
  onMapClick = () => console.log('Map clicked'),

  // Visibility toggles
  showViewProfile = true
}) => {
  const [copied, setCopied] = useState(false);
  
  // Copy vehicle ID to clipboard
  const handleCopyVehicleId = async () => {
    const idToCopy = vehicleId || ambulanceId;
    try {
      await navigator.clipboard.writeText(idToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  // --- Utility Logic ---

  // Status Badge Configuration
  const getStatusBadge = (status) => {
    const isActive = status.startsWith("Active");
    const badgeStyle = isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700";
    return (
      <span className={`text-xs font-medium px-2 py-0.5 rounded-sm ${badgeStyle}`}>
        {status}
      </span>
    );
  };

  // Map border color logic (dynamically changes based on status/journey)
  const getMapBorderColor = () => {
    // Top card (Round Journey) has a GREEN map border
    if (status === "Active Round Journey") {
      return "border-green-400";
    }
    // Out Bound / In Bound / Serious/Critical status has a RED map border
    if (status === "Active Out Bound" || status === "Active In Bound" || isCritical) {
      return "border-red-400";
    }
    return "border-gray-400";
  };

  // Vehicle Type Badge Configuration (Cardiology / Advanced / Basic)
  const getVehicleTypeBadgeConfig = (type) => {
    if (!type || type === 'Unknown') {
      return {
        style: 'bg-gray-100 text-gray-700 border-gray-200',
        label: type || 'Unknown'
      };
    }
    const lower = type.toLowerCase();
    if (lower.includes('cardiac')) {
      return {
        style: 'bg-rose-50 text-rose-700 border-rose-200',
        label: type
      };
    }
    if (lower.includes('advanced') || lower.includes('als')) {
      return {
        style: 'bg-purple-50 text-purple-700 border-purple-200',
        label: type
      };
    }
    if (lower.includes('basic') || lower.includes('bls')) {
      return {
        style: 'bg-blue-50 text-blue-700 border-blue-200',
        label: type
      };
    }
    return {
      style: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      label: type
    };
  };
  
  // --- Render ---

  return (
    // **APPLY DYNAMIC STYLING HERE**
    // The main card background and border now use the dynamic props.
    <div className={`${cardBgColor} rounded-lg shadow-md p-4 mb-4 border-2 ${cardBorderColor}`}>
      
      {/* 1. Header Section (ID, Time/Distance, Status/Critical, More) */}
      <div className="flex items-center justify-between mb-4">
        
        {/* Left Side: Plate Number (big), Vehicle Type (badge), Vehicle ID (small), Time, Distance, Duration */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2.5 flex-wrap">
              <Truck className="w-5 h-5 text-gray-600 shrink-0" />
              <span className="font-semibold text-gray-900 text-lg">
                {plateNumber || ambulanceId}
              </span>
              {vehicleType && (
                <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full border inline-flex items-center gap-1 shadow-xs ${getVehicleTypeBadgeConfig(vehicleType).style}`}>
                  <Activity className="w-3 h-3 shrink-0" />
                  <span>{getVehicleTypeBadgeConfig(vehicleType).label}</span>
                </span>
              )}
            </div>
            {vehicleId && (
              <div className="flex items-center gap-2 ml-7">
                <span className="text-xs text-gray-500 font-mono">
                  {vehicleId}
                </span>
                <button
                  onClick={handleCopyVehicleId}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  title="Copy Vehicle ID"
                >
                  {copied ? (
                    <Check className="w-3 h-3 text-green-600" />
                  ) : (
                    <Copy className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Tighter spacing for Time, Distance, Duration */}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Clock className="w-4 h-4 text-gray-400" />
            <span>{time}</span>
            <span className="text-gray-300">•</span>
            <span>{distance}</span>
            <span className="text-gray-300">•</span>
            <span>{duration}</span>
          </div>
        </div>

        {/* Right Side: Status, Critical, More Icon */}
        <div className="flex items-center gap-4">
          {getStatusBadge(status)}

          {/* Critical Status (Uses red text for emphasis) */}
          <div className="text-sm font-medium text-red-600">
             {isCritical ? "Critical" : (status === "Active In Bound" ? "Serious" : "")} 
          </div>

          {/* More Options Icon */}
          <button onClick={onViewMore} className="p-1 text-gray-500 hover:text-gray-700">
            <MoreVertical className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* 2. Main Content Area: Left (Details) and Right (Map) */}
      <div className="flex gap-4 items-start">
        
        {/* Left Side - Details (Camera, Equipment, Driver) */}
        <div className="flex-1 space-y-4">
          
          {/* ICU on Wheels / Camera Section */}
          <div>
            <SectionHeader icon={Camera} title="ICU on Wheels" />
            <div className="flex items-center gap-6 ml-6">
              <CameraStatus label="Dash Camera" isConnected={dashCamera} />
              <CameraStatus label="Driver Camera" isConnected={driverCamera} />
              <CameraStatus label="Patient Camera" isConnected={patientCamera} />
              <button
                onClick={onViewMore}
                className="text-blue-600 text-sm hover:text-blue-800 font-medium whitespace-nowrap"
              >
                View More
              </button>
            </div>
          </div>

          {/* Equipment Status Section */}
          <div>
            <SectionHeader icon={AlertCircle} title="Equipments" />
            <div className="flex gap-2 ml-6">
              <EquipmentPill {...fuel} />
              <EquipmentPill {...oxygen} />
              <EquipmentPill {...alcohol} />
              <EquipmentPill {...nitrogen} />
              <EquipmentPill {...temperature} />
            </div>
          </div>

          {/* Driver Information Section */}
          <div className="space-y-2 pt-1">
            <div className="flex items-center gap-4">
              <User className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-gray-900">{driverName}</span>
              {/* Action Buttons: Simple blue text buttons */}
              <button
                onClick={onCallDriver}
                className="text-blue-600 text-sm hover:text-blue-800 font-medium"
              >
                Call Now
              </button>
              <button
                onClick={onViewHistory}
                className="text-blue-600 text-sm hover:text-blue-800 font-medium"
              >
                History
              </button>
              {showViewProfile && (
                <button
                  onClick={onViewProfile}
                  className="text-blue-600 text-sm hover:text-blue-800 font-medium"
                >
                  View Profile
                </button>
              )}
            </div>

            {/* Response Time and Ratings */}
            <div className="flex items-center gap-6 text-sm text-gray-600 ml-6">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-gray-500" />
                <span>Avg. Response Time <span className="font-medium text-gray-800">{avgResponseTime}</span></span>
              </div>

              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 text-gray-500" />
                <span>Ratings <span className="font-medium text-gray-800">{rating}</span></span>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Right Side - Embedded Map */}
        <div className="w-56 h-48 flex-shrink-0">
          <div
            className={`w-full h-full rounded-lg border-2 ${getMapBorderColor()} bg-gray-100 flex flex-col items-center justify-center cursor-pointer relative`}
            onClick={onMapClick}
          >
             <MapPin className="w-6 h-6 text-red-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
             <div className="text-center absolute bottom-2 w-full px-1">
              <div className="text-xs font-medium text-gray-800 leading-tight">{mapLocation}</div>
              <div className="text-xs text-gray-600 leading-tight">{mapSubLocation}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmbulanceCard;